import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiEdit2, FiTrash2, FiClock, FiMap } from 'react-icons/fi';
import { geofenceService } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import GeofenceMap from '../components/map/GeofenceMap';

const GeofenceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [geofence, setGeofence] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('details');

  // Fetch geofence data
  useEffect(() => {
    const fetchGeofenceData = async () => {
      try {
        setLoading(true);
        
        // In a real app, we would fetch from API
        // const response = await geofenceService.getGeofence(id);
        // const vehiclesResponse = await geofenceService.getVehiclesInGeofence(id);
        
        // For now, use dummy data
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Dummy geofence data based on ID
        let dummyGeofence;
        let dummyVehicles = [];
        
        if (id === 'g1') {
          dummyGeofence = {
            id: 'g1',
            name: 'Downtown Zone',
            description: 'Main commercial area coverage',
            type: 'circle',
            center: { lat: 40.7128, lng: -74.006 },
            radius: 1500, // in meters
            alertType: 'both',
            isActive: true,
            createdAt: '2023-06-15T10:30:00Z',
            createdBy: {
              id: 'u1',
              name: 'Admin User'
            }
          };
          
          dummyVehicles = [
            {
              id: 'v1',
              name: 'Truck 101',
              registration: 'ABC-1234',
              currentPosition: { lat: 40.7128, lng: -74.006 },
              currentSpeed: 35,
              status: 'active',
              lastUpdate: new Date().toISOString()
            }
          ];
        } else if (id === 'g2') {
          dummyGeofence = {
            id: 'g2',
            name: 'Airport Perimeter',
            description: 'Airport restricted access zone',
            type: 'polygon',
            vertices: [
              { lat: 40.6413, lng: -73.7781 },
              { lat: 40.6631, lng: -73.7675 },
              { lat: 40.6542, lng: -73.8101 },
              { lat: 40.6381, lng: -73.7948 }
            ],
            alertType: 'entry',
            isActive: true,
            createdAt: '2023-06-20T14:15:00Z',
            createdBy: {
              id: 'u1',
              name: 'Admin User'
            }
          };
        } else if (id === 'g3') {
          dummyGeofence = {
            id: 'g3',
            name: 'Warehouse District',
            description: 'Main logistics hub',
            type: 'circle',
            center: { lat: 40.7234, lng: -74.0123 },
            radius: 800, // in meters
            alertType: 'both',
            isActive: true,
            createdAt: '2023-07-05T09:45:00Z',
            createdBy: {
              id: 'u1',
              name: 'Admin User'
            }
          };
          
          dummyVehicles = [
            {
              id: 'v2',
              name: 'Delivery Van 202',
              registration: 'XYZ-5678',
              currentPosition: { lat: 40.7236, lng: -74.0120 },
              currentSpeed: 0,
              status: 'idle',
              lastUpdate: new Date().toISOString()
            }
          ];
        } else {
          // If ID doesn't match any dummy data, simulate not found
          throw new Error('Geofence not found');
        }

        setGeofence(dummyGeofence);
        setVehicles(dummyVehicles);
        setLoading(false);
      } catch (err) {
        setError(err.message || 'Failed to load geofence');
        setLoading(false);
        console.error('Error fetching geofence:', err);
      }
    };

    fetchGeofenceData();
  }, [id]);

  // Handle delete geofence
  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this geofence?')) {
      try {
        // In a real app, we would call API
        // await geofenceService.deleteGeofence(id);
        
        // Navigate back to geofences list
        navigate('/geofences');
      } catch (err) {
        console.error('Error deleting geofence:', err);
      }
    }
  };

  // Handle status toggle
  const handleToggleStatus = async () => {
    try {
      // In a real app, we would call API
      // await geofenceService.updateGeofence(id, { 
      //   isActive: !geofence.isActive 
      // });
      
      // For demo, just update local state
      setGeofence(prev => ({
        ...prev,
        isActive: !prev.isActive
      }));
    } catch (err) {
      console.error('Error updating geofence status:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-10">
        <p className="text-ui-error">{error}</p>
        <Link to="/geofences" className="mt-4 btn btn-primary">
          Back to Geofences
        </Link>
      </div>
    );
  }

  if (!geofence) {
    return (
      <div className="text-center py-10">
        <p className="text-gray-500">Geofence not found</p>
        <Link to="/geofences" className="mt-4 btn btn-primary">
          Back to Geofences
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center">
          <Link to="/geofences" className="mr-4">
            <FiArrowLeft className="text-gray-500 hover:text-primary" size={20} />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{geofence.name}</h1>
            <p className="text-gray-600">{geofence.description}</p>
          </div>
        </div>
        <div className="flex space-x-3">
          <button 
            onClick={handleToggleStatus}
            className={`btn ${geofence.isActive ? 'btn-outline' : 'btn-primary'}`}
          >
            {geofence.isActive ? 'Deactivate' : 'Activate'}
          </button>
          <Link to={`/geofences/${id}/edit`} className="btn btn-outline flex items-center">
            <FiEdit2 className="mr-2" />
            Edit
          </Link>
          <button 
            onClick={handleDelete}
            className="btn bg-ui-error text-white hover:bg-red-700 flex items-center"
          >
            <FiTrash2 className="mr-2" />
            Delete
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b">
          <nav className="flex">
            <button
              className={`px-4 py-3 text-sm font-medium border-b-2 ${
                activeTab === 'details'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('details')}
            >
              Details
            </button>
            <button
              className={`px-4 py-3 text-sm font-medium border-b-2 ${
                activeTab === 'vehicles'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('vehicles')}
            >
              Vehicles ({vehicles.length})
            </button>
          </nav>
        </div>

        {/* Tab content */}
        <div className="p-6">
          {activeTab === 'details' ? (
            <div className="space-y-6">
              {/* Geofence map */}
              <div className="h-80 bg-gray-100 rounded">
                <GeofenceMap geofence={geofence} />
              </div>
              
              {/* Geofence details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Geofence Information</h3>
                  
                  <div>
                    <p className="text-sm text-gray-500">Type</p>
                    <p className="font-medium">{geofence.type.charAt(0).toUpperCase() + geofence.type.slice(1)}</p>
                  </div>
                  
                  {geofence.type === 'circle' && (
                    <>
                      <div>
                        <p className="text-sm text-gray-500">Center</p>
                        <p className="font-medium">{geofence.center.lat.toFixed(6)}, {geofence.center.lng.toFixed(6)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Radius</p>
                        <p className="font-medium">{geofence.radius} meters</p>
                      </div>
                    </>
                  )}
                  
                  {geofence.type === 'polygon' && (
                    <div>
                      <p className="text-sm text-gray-500">Vertices</p>
                      <div className="text-sm max-h-40 overflow-y-auto">
                        {geofence.vertices.map((vertex, index) => (
                          <p key={index} className="font-mono">
                            {index + 1}: {vertex.lat.toFixed(6)}, {vertex.lng.toFixed(6)}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <p className="text-sm text-gray-500">Alert Type</p>
                    <p className="font-medium">{geofence.alertType.charAt(0).toUpperCase() + geofence.alertType.slice(1)}</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Status Information</h3>
                  
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    <p className={`font-medium ${geofence.isActive ? 'text-ui-success' : 'text-gray-500'}`}>
                      {geofence.isActive ? 'Active' : 'Inactive'}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Created By</p>
                    <p className="font-medium">{geofence.createdBy.name}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Created At</p>
                    <p className="font-medium">{new Date(geofence.createdAt).toLocaleString()}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Current Vehicles Inside</p>
                    <p className="font-medium">{vehicles.length}</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div>
              {/* Vehicles tab */}
              {vehicles.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No vehicles currently inside this geofence
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Vehicle
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Speed
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Last Update
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {vehicles.map(vehicle => (
                        <tr key={vehicle.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="ml-2">
                                <div className="text-sm font-medium text-gray-900">{vehicle.name}</div>
                                <div className="text-sm text-gray-500">{vehicle.registration}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              vehicle.status === 'active' ? 'bg-green-100 text-green-800' : 
                              vehicle.status === 'idle' ? 'bg-yellow-100 text-yellow-800' :
                              vehicle.status === 'stopped' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {vehicle.status.charAt(0).toUpperCase() + vehicle.status.slice(1)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {vehicle.currentSpeed} mph
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(vehicle.lastUpdate).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <Link to={`/vehicles/${vehicle.id}`} className="text-primary hover:text-primary-dark">
                              View
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GeofenceDetail;